import { QueryClient } from "@tanstack/react-query";

/**
 * Factory pour créer un QueryClient.
 *
 * On expose une factory (et pas juste un singleton) pour deux raisons :
 *  - Côté SSR (le jour où on migrera vers Next.js / Node), chaque requête
 *    doit avoir son propre QueryClient afin d'éviter de partager le cache
 *    entre utilisateurs.
 *  - Côté tests, ça permet d'instancier un client neuf par test.
 *
 * En production client, on garde un singleton pour préserver le cache
 * pendant toute la session navigateur (cf. `getQueryClient` plus bas).
 */
export const makeQueryClient = () =>
    new QueryClient({
        defaultOptions: {
            queries: {
                // On évite de refetch automatiquement à chaque focus pendant
                // qu'on migre progressivement : comportement proche de l'ancien
                // legacy async hook behavior. Revisit after full migration.
                refetchOnWindowFocus: false,
                // 1 min : les données restent "fresh" ce temps là, donc on ne
                // spam pas l'API si l'utilisateur navigue vite.
                staleTime: 60 * 1000,
                // 5 min en cache mémoire après que le dernier observer est parti.
                gcTime: 5 * 60 * 1000,
                retry: 1,
            },
            mutations: {
                retry: 0,
            },
        },
    });

let browserQueryClient: QueryClient | undefined;

/**
 * Retourne le QueryClient à utiliser côté navigateur (singleton).
 *
 * Côté serveur (typeof window === "undefined") on renverrait un nouveau
 * client à chaque requête — utile dès qu'on aura un runtime Node qui fait
 * du SSR. Tant qu'on est en SPA pure, seul le chemin navigateur est exécuté.
 */
export const getQueryClient = (): QueryClient => {
    if (typeof window === "undefined") {
        // Pas encore utilisé (CRA = client only), mais prêt pour un futur SSR.
        return makeQueryClient();
    }

    if (!browserQueryClient) {
        browserQueryClient = makeQueryClient();
    }

    return browserQueryClient;
};
